import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { Mail, Lock, User, Phone, ArrowRight, Loader2, CreditCard } from 'lucide-react';
import { GlassButton } from '../components/ui/GlassButton';
import logoPro from '../logo_pro.png';

/** Máscara CPF: 000.000.000-00 */
function formatCpfMask(value: string): string {
    const d = value.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function isValidCpf(cpf: string): boolean {
    const d = cpf.replace(/\D/g, '');
    if (d.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(d)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(d[i]) * (10 - i);
    let rest = (sum * 10) % 11;
    if (rest === 10) rest = 0;
    if (rest !== parseInt(d[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(d[i]) * (11 - i);
    rest = (sum * 10) % 11;
    if (rest === 10) rest = 0;
    return rest === parseInt(d[10]);
}

/** Máscara brasileira: +55 (XX) XXXXX-XXXX (celular, 11 dígitos). */
function formatPhoneMask(value: string): string {
    const digits = value.replace(/\D/g, '');
    let d = digits.startsWith('55') ? digits.slice(2) : digits;
    d = d.slice(0, 11);
    if (d.length === 0) return '+55 ';
    if (d.length <= 2) return `+55 (${d})`;
    if (d.length <= 7) return `+55 (${d.slice(0, 2)}) ${d.slice(2)}`;
    return `+55 (${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** Extrai dígitos e retorna +55XXXXXXXXXXX para envio. */
function phoneToPayload(masked: string): string {
    let digits = masked.replace(/\D/g, '');
    if (digits.startsWith('55')) digits = digits.slice(2);
    digits = digits.slice(0, 11);
    return digits.length >= 10 ? `+55${digits}` : '';
}

export const Register: React.FC = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('+55 ');
    const [cpf, setCpf] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(formatPhoneMask(e.target.value));
    };

    const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCpf(formatCpfMask(e.target.value));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const phonePayload = phoneToPayload(phone);
        if (!phonePayload || phonePayload.length < 12) {
            setError('Informe um telefone válido com DDD (ex: 11 99999-9999).');
            return;
        }
        const cpfDigits = cpf.replace(/\D/g, '');
        if (cpfDigits.length !== 11 || !isValidCpf(cpfDigits)) {
            setError('Informe um CPF válido (11 dígitos).');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await api.post('/auth/register', {
                full_name: name.trim(),
                email: email.trim(),
                password,
                phone: phonePayload,
                cpf: cpfDigits
            });
            navigate('/login');
        } catch (err: any) {
            const status = err.response?.status;
            if (status === 422 || status === 400) {
                setError(err.response?.data?.error || 'Verifique os dados informados e tente novamente.');
            } else if (status === 409) {
                setError('Este e-mail j\u00e1 est\u00e1 cadastrado. Fa\u00e7a login.');
            } else {
                setError(err.response?.data?.error || 'N\u00e3o foi poss\u00edvel criar a conta. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page min-h-screen flex items-center justify-center p-4">
            <div className="auth-card w-full max-w-md bg-white/5 border border-white/10 rounded-[32px] p-10 backdrop-blur-xl relative overflow-hidden">
                <div className="absolute top-[-50%] left-[50%] -translate-x-1/2 w-80 h-80 bg-orange-500/10 rounded-full blur-[80px] pointer-events-none" />

                <div className="relative z-10">
                    <div className="text-center mb-10">
                        <img src={logoPro} alt="Hotlize" className="h-10 mx-auto mb-6" />
                        <h1 className="text-2xl font-bold text-white mb-2">Crie sua conta</h1>
                        <p className="text-gray-400 text-sm">Comece a gerenciar seus bots hoje mesmo</p>
                    </div>

                    {/* Badge de trial */}
                    <div className="flex items-center justify-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-semibold px-4 py-2 rounded-xl mb-6">
                        <span>✦</span>
                        <span>7 dias grátis — acesso completo, sem cartão de crédito</span>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-500 p-4 rounded-xl mb-6 text-sm text-center">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="input-group-pro">
                            <label>NOME COMPLETO</label>
                            <div className="input-wrapper">
                                <User size={18} className="text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Seu nome"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-group-pro">
                            <label>E-MAIL</label>
                            <div className="input-wrapper">
                                <Mail size={18} className="text-gray-500" />
                                <input
                                    type="email"
                                    placeholder="seu@email.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-group-pro">
                            <label>TELEFONE</label>
                            <div className="input-wrapper">
                                <Phone size={18} className="text-gray-500" />
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    placeholder="+55 (11) 99999-9999"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    required
                                />
                            </div>
                            <span className="text-[10px] text-gray-500 mt-1 block">Com DDD. Celular: (11) 99999-9999</span>
                        </div>

                        <div className="input-group-pro">
                            <label>CPF</label>
                            <div className="input-wrapper">
                                <CreditCard size={18} className="text-gray-500" />
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    placeholder="000.000.000-00"
                                    value={cpf}
                                    onChange={handleCpfChange}
                                    required
                                />
                            </div>
                            <span className="text-[10px] text-gray-500 mt-1 block">Apenas números (11 dígitos). Usado para gerar o PIX.</span>
                        </div>

                        <div className="input-group-pro">
                            <label>SENHA</label>
                            <div className="input-wrapper">
                                <Lock size={18} className="text-gray-500" />
                                <input
                                    type="password"
                                    placeholder="Mínimo 6 caracteres"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>

                        <GlassButton type="submit" className="w-full h-14" loading={loading}>
                            {loading ? <Loader2 className="animate-spin" /> : <>CRIAR CONTA <ArrowRight size={18} /></>}
                        </GlassButton>
                    </form>

                    <div className="mt-8 text-center text-sm">
                        <span className="text-gray-500">Já tem uma conta? </span>
                        <Link to="/login" className="text-orange-500 font-bold hover:text-orange-400 transition-colors">
                            Fazer login
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};
